import { getDayBoundsSAST } from '@/lib/sastTime';
import { isTourVideoCaptureMode } from '@/lib/tourCaptureMode';
import { resolveWorkItemStatus } from '@/lib/work-items/workItemStatus';
import type { WorkItem, SmartTaskConfig, JobFilter, SortOption } from '../types';
import { isSmartTaskTerminalStatus } from './templateActionNormalization';
import { getWorkItemTypeRegistry } from '@/config/work-items/configLoader';

export const SMART_TASK_TODAY_WINDOW_HOURS = 12;
export const SMART_TASK_SOURCE_CACHE_KEY = 'smarttask-assigned-source';
export const SMART_TASK_ASSIGNED_SOURCE_LIMIT = 200;
/** Smaller initial payload so the first paint after a cache clear stays fast.
 * Mirrors the legacy "today" toolbar fetch size that historically loaded instantly. */
export const SMART_TASK_ASSIGNED_FAST_SOURCE_LIMIT = 50;
const SMART_TASK_MS_PER_DAY = 24 * 60 * 60 * 1000;
/** @deprecated Use getSmartTaskClosedStatusSet() for SSOT-backed lookups. Kept as fallback. */
export const SMART_TASK_CLOSED_STATUSES = [
  'completed',
  'resolved',
  'cancelled',
  'closed',
  'failed',
  'skipped',
  'ai_review',
  'ai_on_hold',
] as const;
/** @deprecated Use getSmartTaskOpenStatusSet() for SSOT-backed lookups. Kept as fallback. */
export const SMART_TASK_OPEN_STATUSES = [
  'open',
  'scheduled',
  'assigned',
  'confirmed',
  'pending',
  'dispatched',
] as const;
/** @deprecated Use getSmartTaskActiveStatusSet() for SSOT-backed lookups. Kept as fallback. */
export const SMART_TASK_ACTIVE_STATUSES = [
  'checked_in',
  'in_progress',
  'working',
  'paused',
  'started',
  'en_route',
  'on_route',
  'travel_paused',
  'on_site',
  'traveling',
  'arrived',
] as const;

const FALLBACK_CLOSED_SET = new Set<string>(SMART_TASK_CLOSED_STATUSES);
const FALLBACK_OPEN_SET = new Set<string>(SMART_TASK_OPEN_STATUSES);
const FALLBACK_ACTIVE_SET = new Set<string>(SMART_TASK_ACTIVE_STATUSES);

let _ssotClosedSet: Set<string> | null = null;
let _ssotOpenSet: Set<string> | null = null;
let _ssotActiveSet: Set<string> | null = null;

function getClosedStatusSet(): Set<string> {
  if (_ssotClosedSet) return _ssotClosedSet;
  try {
    const registry = getWorkItemTypeRegistry();
    if (registry.isInitialized()) {
      const ssotStatuses = registry.getOperationalStatusesByCategory('closed');
      if (ssotStatuses.length > 0) {
        _ssotClosedSet = new Set(ssotStatuses);
        return _ssotClosedSet;
      }
    }
  } catch { /* fallback below */ }
  return FALLBACK_CLOSED_SET;
}

function getOpenStatusSet(): Set<string> {
  if (_ssotOpenSet) return _ssotOpenSet;
  try {
    const registry = getWorkItemTypeRegistry();
    if (registry.isInitialized()) {
      const ssotStatuses = registry.getOperationalStatusesByCategory('open');
      if (ssotStatuses.length > 0) {
        _ssotOpenSet = new Set(ssotStatuses);
        return _ssotOpenSet;
      }
    }
  } catch { /* fallback below */ }
  return FALLBACK_OPEN_SET;
}

function getActiveStatusSet(): Set<string> {
  if (_ssotActiveSet) return _ssotActiveSet;
  try {
    const registry = getWorkItemTypeRegistry();
    if (registry.isInitialized()) {
      const ssotStatuses = registry.getOperationalStatusesByCategory('active');
      if (ssotStatuses.length > 0) {
        _ssotActiveSet = new Set(ssotStatuses);
        return _ssotActiveSet;
      }
    }
  } catch { /* fallback below */ }
  return FALLBACK_ACTIVE_SET;
}

type SmartTaskMetadataRecord = Record<string, unknown>;

export function normalizeSmartTaskStatusToken(value: unknown): string {
  return typeof value === 'string'
    ? value.trim().toLowerCase().replace(/[\s-]+/g, '_')
    : '';
}

export function normalizeWorkflowItemType(value: unknown): string {
  return typeof value === 'string'
    ? value.trim().toLowerCase()
    : '';
}

export function supportsAppointmentsInWorkflow(config: SmartTaskConfig): boolean {
  const knownAppointmentTypes = new Set(['appointment']);
  const workflowConfigs = [config, ...(config.workflowTemplates || [])];

  return workflowConfigs.some((workflowConfig) => {
    const configuredItemTypes = [
      ...(workflowConfig.allowedItemTypes || []),
      ...(workflowConfig.queryItemTypes || []),
      ...((workflowConfig.beginAction?.startActions || []).flatMap((action) => [action.itemType, action.displayItemType])),
      ...((workflowConfig.beginAction?.workflowSections || []).flatMap((section) => (
        (section.actions || []).flatMap((action) => action.itemTypes || [])
      ))),
    ];

    return configuredItemTypes.some((itemType) => knownAppointmentTypes.has(normalizeWorkflowItemType(itemType)));
  });
}

export function getSmartTaskResolvedTerminalStatus(item: WorkItem): string | null {
  const itemRecord = item as unknown as SmartTaskMetadataRecord;
  const customFields = item.custom_fields as SmartTaskMetadataRecord | null | undefined;
  const resolution = normalizeSmartTaskStatusToken(
    itemRecord.resolution_type
    ?? customFields?.resolution_type
    ?? customFields?.resolution
    ?? customFields?.completion_resolution,
  );
  const hasCompletionMarker = Boolean(
    itemRecord.resolved_at
    || itemRecord.completed_at
    || customFields?.resolved_at
    || customFields?.completed_at
    || customFields?.check_out_timestamp,
  );

  if (!hasCompletionMarker && !resolution) {
    return null;
  }

  if (resolution === 'resolved') {
    return 'resolved';
  }

  if (resolution === 'rescheduled') {
    return 'rescheduled';
  }

  if (resolution === 'unresolved' || resolution === 'partial') {
    return 'unresolved';
  }

  if (getClosedStatusSet().has(resolution)) {
    return resolution;
  }

  return hasCompletionMarker ? normalizeSmartTaskStatusToken(item.status) || 'closed' : null;
}

export function getSmartTaskFilterVisibleStatus(item: WorkItem): string {
  return resolveWorkItemStatus(item as unknown as Record<string, unknown>, {
    includeTimingWarnings: false,
  }).baseStatusKey;
}

export function isSmartTaskClosedForFilter(item: WorkItem): boolean {
  const status = getSmartTaskFilterVisibleStatus(item);
  return isSmartTaskTerminalStatus(status) || getClosedStatusSet().has(status);
}

function getSmartTaskClosedChangedAtMs(item: WorkItem): number | null {
  const itemRecord = item as unknown as SmartTaskMetadataRecord;
  const customFields = item.custom_fields as SmartTaskMetadataRecord | null | undefined;
  const metadata = item.metadata as SmartTaskMetadataRecord | null | undefined;
  const candidates = [
    itemRecord.resolved_at,
    itemRecord.completed_at,
    itemRecord.closed_at,
    customFields?.resolved_at,
    customFields?.completed_at,
    customFields?.closed_at,
    customFields?.check_out_timestamp,
    customFields?.field_action_completed_at,
    metadata?.resolved_at,
    metadata?.completed_at,
    metadata?.closed_at,
    metadata?.status_changed_at,
    metadata?.field_action_completed_at,
  ];

  for (const candidate of candidates) {
    if (typeof candidate !== 'string' || candidate.trim().length === 0) {
      continue;
    }

    const parsed = new Date(candidate);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.getTime();
    }
  }

  return null;
}

export function shouldIncludeSmartTaskClosedItemWithinRetention(
  item: WorkItem,
  config?: Pick<SmartTaskConfig, 'showClosedItems' | 'closedItemRetentionDays'> | null,
  nowMs: number = Date.now(),
): boolean {
  if (!isSmartTaskClosedForFilter(item)) {
    return false;
  }

  if (config?.showClosedItems === false) {
    return false;
  }

  const retentionDays = Math.max(0, Number(config?.closedItemRetentionDays) || 7);
  if (retentionDays <= 0) {
    return false;
  }

  const closedChangedAtMs = getSmartTaskClosedChangedAtMs(item);
  if (closedChangedAtMs == null) {
    return false;
  }

  const retentionWindowMs = retentionDays * 24 * 60 * 60 * 1000;
  return closedChangedAtMs >= (nowMs - retentionWindowMs);
}

export function isSmartTaskOpenForFilter(item: WorkItem): boolean {
  const status = getSmartTaskFilterVisibleStatus(item);
  return getOpenStatusSet().has(status);
}

export function isSmartTaskOperationallyActiveStatus(status: unknown): boolean {
  return getActiveStatusSet().has(normalizeSmartTaskStatusToken(status));
}

function getDateOnlyDueDateEndSast(value: string): Date | null {
  const trimmedValue = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
    return null;
  }

  const parsed = new Date(`${trimmedValue}T23:59:59.999+02:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function getSmartTaskFilterAnchorTime(item: WorkItem): number | null {
  const dateCandidates = [
    item.scheduled_start,
    item.due_date,
    item.scheduled_end,
    item.created_at,
  ];

  for (let index = 0; index < dateCandidates.length; index += 1) {
    const candidate = dateCandidates[index];
    if (typeof candidate !== 'string' || candidate.trim().length === 0) {
      continue;
    }

    const parsed = index === 1
      ? (getDateOnlyDueDateEndSast(candidate) ?? new Date(candidate))
      : new Date(candidate);

    if (!Number.isNaN(parsed.getTime())) {
      return parsed.getTime();
    }
  }

  return null;
}

export function isSmartTaskWithinTodayWindow(
  item: WorkItem,
  nowMs: number = Date.now(),
): boolean {
  const anchorTime = getSmartTaskFilterAnchorTime(item);
  if (anchorTime == null) {
    return false;
  }

  const rangeMs = SMART_TASK_TODAY_WINDOW_HOURS * 60 * 60 * 1000;
  return anchorTime >= (nowMs - rangeMs) && anchorTime <= (nowMs + rangeMs);
}

export function shouldIncludeSmartTaskWorkItemForFilter(
  item: WorkItem,
  filter: JobFilter,
  config?: Pick<SmartTaskConfig, 'showClosedItems' | 'closedItemRetentionDays'> | null,
  nowMs: number = Date.now(),
): boolean {
  const isClosed = isSmartTaskClosedForFilter(item);

  if (filter === 'completed') {
    // For service jobs, closed includes everything not in the open status set
    if (item.item_type === 'job') {
      const status = normalizeSmartTaskStatusToken(item.status);
      const serviceJobOpenStatuses = new Set(['open', 'scheduled', 'in_progress', 'apt_reschedule', 'apt_partial', 'rescheduled']);
      // Only include if status is NOT in open set AND passes retention check
      if (serviceJobOpenStatuses.has(status)) {
        return false; // These are open, not closed
      }
      return shouldIncludeSmartTaskClosedItemWithinRetention(item, config, nowMs);
    }
    return shouldIncludeSmartTaskClosedItemWithinRetention(item, config, nowMs);
  }

  if (filter === 'today') {
    return !isClosed && isSmartTaskWithinTodayWindow(item, nowMs);
  }

  if (filter === 'incomplete') {
    // For service jobs, open status includes: open, scheduled, in_progress, apt_reschedule, apt_partial, rescheduled
    // Use raw status directly, not the transformed status from resolveWorkItemStatus
    if (item.item_type === 'job') {
      const status = normalizeSmartTaskStatusToken(item.status);
      const serviceJobOpenStatuses = new Set(['open', 'scheduled', 'in_progress', 'apt_reschedule', 'apt_partial', 'rescheduled']);
      return serviceJobOpenStatuses.has(status);
    }
    return isSmartTaskOpenForFilter(item);
  }

  if (filter === 'scheduled') {
    return isSmartTaskOpenForFilter(item);
  }

  if (filter === 'urgent') {
    return normalizeSmartTaskStatusToken(item.priority) === 'urgent'
      || normalizeSmartTaskStatusToken(item.priority) === 'high';
  }

  return true;
}

export function matchesSmartTaskSearch(
  item: WorkItem,
  itemsById: Map<string, WorkItem>,
  normalizedSearchQuery: string,
): boolean {
  if (!normalizedSearchQuery) {
    return true;
  }

  const parentJob = item.parent_id ? itemsById.get(String(item.parent_id)) : null;
  const customFields = item.custom_fields || {};
  const values = [
    item.title,
    item.item_number,
    item.description,
    item.contact_name,
    item.customer_name,
    item.location_name,
    item.location_address,
    item.parent_title,
    item.parent_job_number,
    item.parent_job_title,
    typeof customFields.appointment_number === 'string' ? customFields.appointment_number : undefined,
    typeof customFields.parent_job_number === 'string' ? customFields.parent_job_number : undefined,
    typeof customFields.parent_job_title === 'string' ? customFields.parent_job_title : undefined,
    parentJob?.item_number,
    parentJob?.title,
  ];

  return values.some((value) => typeof value === 'string' && value.toLowerCase().includes(normalizedSearchQuery));
}

export function sortSmartTaskWorkItems(items: WorkItem[], sortBy: SortOption): WorkItem[] {
  const priorityRank: Record<string, number> = {
    urgent: 4,
    high: 3,
    medium: 2,
    low: 1,
  };

  const sorted = [...items];
  sorted.sort((left, right) => {
    if (sortBy === 'priority') {
      const leftRank = priorityRank[normalizeSmartTaskStatusToken(left.priority)] || 0;
      const rightRank = priorityRank[normalizeSmartTaskStatusToken(right.priority)] || 0;
      if (leftRank !== rightRank) {
        return rightRank - leftRank;
      }
    } else if (sortBy === 'status') {
      const byStatus = getSmartTaskFilterVisibleStatus(left).localeCompare(getSmartTaskFilterVisibleStatus(right));
      if (byStatus !== 0) {
        return byStatus;
      }
    } else if (sortBy === 'customer') {
      const byCustomer = String(left.customer_name || left.contact_name || '').localeCompare(
        String(right.customer_name || right.contact_name || ''),
      );
      if (byCustomer !== 0) {
        return byCustomer;
      }
    } else {
      const leftTime = getSmartTaskFilterAnchorTime(left) ?? 0;
      const rightTime = getSmartTaskFilterAnchorTime(right) ?? 0;
      if (leftTime !== rightTime) {
        return sortBy === 'date_desc' ? rightTime - leftTime : leftTime - rightTime;
      }
    }

    return String(left.title || left.item_number || left.id).localeCompare(String(right.title || right.item_number || right.id));
  });

  return sorted;
}

export function buildSmartTaskAssignedSourceParams(args: {
  config: SmartTaskConfig;
  demoTrainingOnly: boolean;
  /** 'fast' returns today's open items only with a smaller limit so the first paint
   * lands quickly after a cache clear. 'full' (default) returns the wider source
   * snapshot the toolbar filters operate on. */
  mode?: 'fast' | 'full';
}): URLSearchParams {
  const { config, demoTrainingOnly, mode = 'full' } = args;
  const params = new URLSearchParams();

  const queryItemTypes = [...(config.queryItemTypes?.length
    ? config.queryItemTypes
    : config.allowedItemTypes || [])];

  if (supportsAppointmentsInWorkflow(config)
    && !queryItemTypes.some((itemType) => normalizeWorkflowItemType(itemType) === 'appointment')
  ) {
    queryItemTypes.push('appointment');
  }

  const hasExplicitNoQueryItemTypes = (
    Array.isArray(config.queryItemTypes) && config.queryItemTypes.length === 0
  ) || (
    !config.queryItemTypes && Array.isArray(config.allowedItemTypes) && config.allowedItemTypes.length === 0
  );

  if (hasExplicitNoQueryItemTypes) {
    params.set('item_type', '__none__');
  } else if (queryItemTypes.length > 0) {
    params.set('item_type', queryItemTypes.join(','));
  }

  if (!config.features.viewAllJobs && !isTourVideoCaptureMode()) {
    params.set('filter_by_assigned_user', 'true');
    params.set('field_worker_mode', 'true');
  }

  params.set('sort_by', 'scheduled_start');
  params.set('sort_order', 'asc');

  if (mode === 'fast') {
    const todayBounds = getDayBoundsSAST();
    params.set('scheduled_start_from', todayBounds.start);
    params.set('scheduled_start_to', todayBounds.end);
    params.set('status_not_in', SMART_TASK_CLOSED_STATUSES.join(','));
    params.set('limit', String(SMART_TASK_ASSIGNED_FAST_SOURCE_LIMIT));
  } else {
    params.set('limit', String(SMART_TASK_ASSIGNED_SOURCE_LIMIT));
  }

  if (demoTrainingOnly) {
    params.set('demo_training_only', 'true');
  }

  return params;
}

export function buildSmartTaskRecentClosedSourceParams(args: {
  config: Pick<SmartTaskConfig, 'allowedItemTypes' | 'queryItemTypes' | 'features' | 'showClosedItems' | 'closedItemRetentionDays'>;
  demoTrainingOnly: boolean;
  nowMs?: number;
}): URLSearchParams | null {
  const { config, demoTrainingOnly, nowMs = Date.now() } = args;
  const retentionDays = Math.max(0, Number(config.closedItemRetentionDays) || 7);

  if (config.showClosedItems === false || retentionDays <= 0) {
    return null;
  }

  const params = new URLSearchParams();
  const queryItemTypes = [...(config.queryItemTypes?.length
    ? config.queryItemTypes
    : config.allowedItemTypes || [])];

  const hasExplicitNoQueryItemTypes = (
    Array.isArray(config.queryItemTypes) && config.queryItemTypes.length === 0
  ) || (
    !config.queryItemTypes && Array.isArray(config.allowedItemTypes) && config.allowedItemTypes.length === 0
  );

  if (hasExplicitNoQueryItemTypes) {
    params.set('item_type', '__none__');
  } else if (queryItemTypes.length > 0) {
    params.set('item_type', queryItemTypes.join(','));
  }

  if (!config.features.viewAllJobs && !isTourVideoCaptureMode()) {
    params.set('filter_by_assigned_user', 'true');
    params.set('field_worker_mode', 'true');
  }

  params.set('sort_by', 'completed_at');
  params.set('sort_order', 'desc');
  params.set('completed_from', new Date(nowMs - (retentionDays * SMART_TASK_MS_PER_DAY)).toISOString());
  params.set('completed_to', new Date(nowMs).toISOString());
  params.set('limit', String(SMART_TASK_ASSIGNED_SOURCE_LIMIT));

  if (demoTrainingOnly) {
    params.set('demo_training_only', 'true');
  }

  return params;
}

export function buildSmartTaskTodayWindowParams(): { scheduledStartFrom: string; scheduledStartTo: string } {
  const bounds = getDayBoundsSAST();
  return {
    scheduledStartFrom: bounds.start,
    scheduledStartTo: bounds.end,
  };
}